/**
 * User-defined "big screen" dashboards, persisted to localStorage until a backend
 * API exists. A dashboard is a named, wall-display view the user assembles from
 * widgets over a set of venues.
 *
 * Phase 1 only captures the name/description and an (empty) widget list; the widget
 * shapes below are defined up front so later phases can populate them without a
 * storage migration.
 */

/** The kinds of widget a big-screen dashboard can hold. */
export type DashboardWidgetType =
  /** A single large live number for one headline metric. */
  | 'statTile'
  /** The scan-rate-by-entrance grid (respects entrance groups). */
  | 'scanRateByEntrance'
  /** The errors-by-result-code grid. */
  | 'errorsByResultCode'
  /** One chart with a line per selected entrance (existing-style graph). */
  | 'multiLineGraph'
  /** One single-line attendance-over-time graph per entrance. */
  | 'entranceGraph'
  /** An uploaded floor-plan image with entrance markers placed over it. */
  | 'venueMap'
  /** A standalone uploaded image (logo, banner, photo) with no markers. */
  | 'image'
  /** Free-form text placed around the dashboard (headings, captions, notes). */
  | 'textNote';

/**
 * Human-readable name for each widget type, used where a widget needs a label but
 * has no custom title — e.g. the edit-mode header bar. Kept here so the picker,
 * config and host stay in agreement rather than each spelling the names out.
 */
export const WIDGET_TYPE_LABELS: Record<DashboardWidgetType, string> = {
  statTile: 'Stat Tile',
  scanRateByEntrance: 'Scan Rate by Entrance',
  errorsByResultCode: 'Errors by Result Code',
  multiLineGraph: 'Multi-line Graph',
  entranceGraph: 'Single Entrance Graph',
  venueMap: 'Venue Map',
  image: 'Image',
  textNote: 'Text',
};

/**
 * A widget's display title: the user's custom title when set, otherwise the
 * default name for its type. Never empty, so it's safe to show in the edit header.
 */
export function widgetDisplayTitle(
  widget: Pick<DashboardWidget, 'type' | 'title'>
): string {
  const custom = widget.title?.trim();
  return custom ? custom : WIDGET_TYPE_LABELS[widget.type];
}

/** Which single figure a `statTile` widget shows. */
export type StatMetric =
  | 'entries'
  | 'inVenue'
  | 'exits'
  | 'reentries'
  | 'internal'
  | 'totalScanRate'
  | 'successRate'
  | 'errorRate';

/**
 * A widget's position and size as percentages of the dashboard viewport (0–100),
 * so a layout scales proportionally across different screen sizes.
 */
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * One entrance marker placed over a `venueMap` widget's image. Its position is
 * stored as a percentage of the image's rendered box (0–100) so a marker stays
 * pinned to the same spot on the floor plan as the widget is resized.
 */
export interface VenueMapMarker {
  /** The entrance whose entries/exits/re-entries this marker displays. */
  entranceName: string;
  /** Horizontal position as a percentage of the image width. */
  x: number;
  /** Vertical position as a percentage of the image height. */
  y: number;
}

/** One widget placed on a dashboard. Optional fields apply per widget type. */
export interface DashboardWidget {
  readonly id: string;
  type: DashboardWidgetType;
  /** Optional display title overriding the widget's default heading. */
  title?: string;
  /**
   * Optional absolute font size (px) for the title/heading text. Unset keeps the
   * responsive default that scales the heading with the widget's box.
   */
  titleFontSize?: number;
  /** Renders the title/heading bold when true. Unset/false is the default weight. */
  titleBold?: boolean;
  /**
   * Custom title/heading colour as a hex string. Unset falls back to the theme's
   * default heading colour.
   */
  titleColor?: string;
  /** Grid position/size in viewport percentages. */
  layout: WidgetLayout;
  /** `statTile`: which figure to show. */
  statMetric?: StatMetric;
  /**
   * `statTile`: when true, the tile drops its card background, border and padding
   * so the figure sits directly on the wall. Unset/false shows the normal card.
   */
  hideBackground?: boolean;
  /**
   * `statTile`: transient carrier used only while configuring — the set of metrics
   * the user checked. The dashboard fans these out into one tile per metric on
   * save, so it is never persisted on a stored widget.
   */
  statMetrics?: StatMetric[];
  /**
   * `scanRateByEntrance` / graph widgets: the entrance (or group) names to show.
   * Unset means "all data points".
   */
  entranceNames?: string[];
  /** `errorsByResultCode`: the result codes to show. Unset means "all". */
  errorCodes?: string[];
  /** `scanRateByEntrance`: whether to sum the venue's entrance groups. */
  useGrouping?: boolean;
  /** Graph widgets: which timed series to plot. */
  graphSource?: 'gate' | 'scanner';
  /**
   * `venueMap` / `image`: the uploaded image as a data URL (downscaled before
   * storage so it fits the localStorage quota). Unset means no image yet.
   */
  imageData?: string;
  /** `venueMap`: the entrance markers placed over the image. */
  markers?: VenueMapMarker[];
  /** `textNote`: the free-form text to display. */
  text?: string;
  /**
   * `textNote`: absolute font size in pixels (Word-style point value), so the text
   * renders at a fixed size regardless of the widget's box. Unset defaults to 32.
   */
  fontSize?: number;
  /** `textNote`: renders the text bold when true. Unset/false is normal weight. */
  bold?: boolean;
  /**
   * `textNote`: custom text colour as a hex string (e.g. `#ff0000`). Unset falls
   * back to the theme's default text colour so it stays legible on the wall.
   */
  textColor?: string;
}

/**
 * Widget types that always render "chromeless" — no card background — so their
 * content sits directly on the dashboard wall. A stat tile is chromeless only when
 * the user turns its background off; see {@link isChromelessWidget}.
 */
const ALWAYS_CHROMELESS_TYPES: ReadonlySet<DashboardWidgetType> = new Set([
  'textNote',
  'venueMap',
  'image',
]);

/**
 * Whether a widget renders without a card, sitting directly on the wall. Shared by
 * the widget host (which strips the card chrome) and the dashboard (which then
 * contrasts the widget's text against the wall rather than the card).
 */
export function isChromelessWidget(
  widget: Pick<DashboardWidget, 'type' | 'hideBackground'>
): boolean {
  return (
    ALWAYS_CHROMELESS_TYPES.has(widget.type) ||
    (widget.type === 'statTile' && !!widget.hideBackground)
  );
}

/** A user's saved big-screen dashboard. */
export interface BigScreenDashboard {
  readonly id: string;
  name: string;
  description?: string;
  /**
   * The venue this dashboard belongs to. A dashboard is only listed on the venue
   * it was created for, so this scopes ownership and never changes after creation.
   */
  readonly venueKey: string;
  /** Venue keys whose live counts this dashboard surfaces. */
  venueKeys: string[];
  /**
   * Optional custom wall background as a hex colour (e.g. `#0b3d91`). Unset falls
   * back to the light/dark theme default.
   */
  backgroundColor?: string;
  /**
   * Optional custom widget-card colour as a hex colour. Unset uses the theme's
   * default card (white in light, slate in dark).
   */
  widgetColor?: string;
  /**
   * The saved light/dark palette for text and default widget cards. Unset means
   * "follow the attendance page theme" when the dashboard is opened.
   */
  darkMode?: boolean;
  /** Widgets making up the dashboard; empty until the phase-2 builder lands. */
  widgets: DashboardWidget[];
  /** ISO timestamps stamped by the caller (scripts can't call Date.now()). */
  createdAt: string;
  updatedAt: string;
}
